import { KeyboardEvent, ReactNode, useEffect, useId, useState } from "react";
import { ClickAwayListener, SvgIcon, Tooltip, TooltipProps } from "@mui/material";
import { CoreIconButton } from "../button/Button";
import { isMobileDevice } from "../../util";
import i18nPackage from "../../i18n/i18n";
import styles from "./InfoIndicator.module.css";
import Info from "./images/info.svg?react";

export type CoreInfoIndicatorProps = {
	hint: ReactNode;
	size?: number;
	color?: string;
	ariaHaspopup?: boolean | "grid" | "dialog" | "menu" | "false" | "true" | "listbox" | "tree";
	isPresetProfile?: boolean;
} & Omit<TooltipProps, "title" | "children" | "id">;

export default function CoreInfoIndicator({
	hint,
	size,
	color = "#42296e",
	ariaHaspopup = "dialog",
	isPresetProfile = false,
	slotProps,
	...rest
}: CoreInfoIndicatorProps) {
	const tooltipId = useId();
	const isTouchScreen = isMobileDevice();
	// MUI closes its own tooltip from a `keydown` listener on `document`, which sits above everything
	// else — so a modal around this indicator saw that same Escape and closed along with the tooltip.
	// Owning the open state lets us close the tooltip from the trigger's own React handler instead:
	// that runs below `document`, so stopPropagation there keeps the key from ever getting out. Closing
	// by blurring the trigger would work too, but inside a modal it hands focus to the focus trap, which
	// drops it on the modal's close button rather than leaving it on the trigger.
	const [tooltipOpen, setTooltipOpen] = useState(false);

	// The popper slot defaults to role="tooltip", which we drop. Pulled out of `rest` and merged rather
	// than set as a plain `slotProps` prop after the spread — that would replace the caller's slotProps
	// wholesale, losing their `tooltip`/`arrow` classNames and falling back to MUI's dark default styling.
	const mergedSlotProps: TooltipProps["slotProps"] = {
		...slotProps,
		popper: {
			...slotProps?.popper,
			role: undefined,
		},
	};

	useEffect(() => {
		if (!isTouchScreen || !tooltipOpen) return;

		const handleScroll = () => setTooltipOpen(false);
		window.addEventListener("scroll", handleScroll, true);

		return () => window.removeEventListener("scroll", handleScroll, true);
	}, [isTouchScreen, tooltipOpen]);

	// Only swallow Escape while the tooltip is actually showing, so it still reaches the modal when the
	// trigger merely has focus.
	const handleTriggerKeyDown = (event: KeyboardEvent) => {
		if (event.key !== "Escape" || !tooltipOpen) return;
		event.stopPropagation();
		setTooltipOpen(false);
	};

	if (isTouchScreen)
		return (
			<ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
				<Tooltip
					open={tooltipOpen}
					onOpen={() => setTooltipOpen(true)}
					onClose={() => setTooltipOpen(false)}
					{...rest}
					id={tooltipId}
					title={hint}
					describeChild
					slotProps={mergedSlotProps}
				>
					<CoreIconButton
						variant="plain"
						className={styles.indicatorButton}
						onClick={() => setTooltipOpen((prev) => !prev)}
						onKeyDown={handleTriggerKeyDown}
						aria-describedby={!isPresetProfile ? tooltipId : undefined}
						aria-label={i18nPackage.t("coreComponents.infoIndicator.moreInfoAriaLabel")}
						aria-haspopup={ariaHaspopup}
						aria-expanded={tooltipOpen}
					>
						<SvgIcon sx={{ fontSize: size, color: color }}>
							<Info />
						</SvgIcon>
					</CoreIconButton>
				</Tooltip>
			</ClickAwayListener>
		);

	return (
		<Tooltip
			open={tooltipOpen}
			onOpen={() => setTooltipOpen(true)}
			onClose={() => setTooltipOpen(false)}
			{...rest}
			id={tooltipId}
			title={hint}
			describeChild
			slotProps={mergedSlotProps}
		>
			<SvgIcon
				className={styles.indicator}
				tabIndex={0}
				focusable="true"
				aria-label={i18nPackage.t("coreComponents.infoIndicator.moreInfoAriaLabel")}
				aria-describedby={tooltipId}
				role="button"
				aria-hidden={undefined}
				sx={{ fontSize: size, color: color }}
				onKeyDown={handleTriggerKeyDown}
			>
				<Info />
			</SvgIcon>
		</Tooltip>
	);
}
